import React, { useEffect, useMemo, useState } from "react";
import { api, formatMoney } from "../lib/api";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Sparkles, TrendingDown } from "lucide-react";

// Industry-typical hidden costs for a like-for-like comparison
// (sourced from public Wise/Remitly transparency reports + US bank wire pricing).
const COMPETITORS = [
  {
    key: "clearway",
    name: "ClearPurse Money",
    fee_percent: 0,          // zero transfer fee
    fee_flat: 0,
    fx_markup: 0,            // displayed rate already reflects our small margin (server quote)
    arrival: "Minutes",
    badge: "You",
    primary: true,
  },
  {
    key: "wu",
    name: "Western Union",
    fee_percent: 0.04,       // ~4%
    fee_flat: 0,
    fx_markup: 0.025,        // ~2.5% FX markup
    arrival: "1–3 days",
  },
  {
    key: "bank",
    name: "US Bank Wire",
    fee_percent: 0,
    fee_flat: 30,            // $25–$45 flat
    fx_markup: 0.035,        // ~3.5% FX markup
    arrival: "3–5 days",
  },
];

const AMOUNT_PRESETS = [100, 250, 500, 1000];

const Compare = () => {
  const [countries, setCountries] = useState([]);
  const [rates, setRates] = useState([]);
  const [country, setCountry] = useState("NG");
  const [amount, setAmount] = useState(500);

  useEffect(() => {
    api.get("/countries").then(r => setCountries(r.data));
    api.get("/fx/rates").then(r => setRates(r.data.rates));
  }, []);

  const midRate = useMemo(() => {
    const row = rates.find(r => r.country_code === country);
    return row?.rate || 0;
  }, [rates, country]);

  const destCurrency = useMemo(() => {
    return countries.find(c => c.code === country)?.currency || "";
  }, [countries, country]);

  const results = useMemo(() => {
    const amt = parseFloat(amount) || 0;
    return COMPETITORS.map(p => {
      const fee = amt * p.fee_percent + p.fee_flat;
      const totalPaid = amt + fee;
      const effectiveRate = midRate * (1 - p.fx_markup);
      const received = amt * effectiveRate;
      return { ...p, fee, totalPaid, effectiveRate, received };
    });
  }, [amount, midRate]);

  const winner = results[0];
  const wu = results[1];
  const savings = winner.received - wu.received;

  return (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20" data-testid="compare-section">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-4">
          <div className="text-xs uppercase tracking-[0.2em] text-[var(--ra-accent)] font-semibold mb-3">No hidden costs</div>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-3">See the difference, dollar for dollar.</h2>
          <p className="text-[var(--ra-muted)] leading-relaxed mb-6">
            Banks and legacy wires sneak a 2–4% FX markup on top of the listed fee.
            We charge zero fees and pass on a strong rate. Adjust the calculator —
            the savings speak for themselves.
          </p>

          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ra-muted)] font-semibold">You send</label>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-2xl font-heading font-bold text-[var(--ra-primary)]">$</span>
                <Input
                  type="number"
                  min="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="h-12 text-2xl font-heading font-bold rounded-md border-[var(--ra-border)]"
                  data-testid="compare-amount-input"
                />
              </div>
              <div className="flex gap-2 mt-2 flex-wrap">
                {AMOUNT_PRESETS.map(p => (
                  <button
                    key={p}
                    onClick={() => setAmount(p)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${
                      parseFloat(amount) === p
                        ? "bg-[var(--ra-primary)] text-white border-[var(--ra-primary)]"
                        : "border-[var(--ra-border)] text-[var(--ra-muted)] hover:border-[var(--ra-primary)]/40"
                    }`}
                    data-testid={`compare-preset-${p}`}
                  >
                    ${p}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-[var(--ra-muted)] font-semibold">To</label>
              <Select value={country} onValueChange={setCountry}>
                <SelectTrigger className="mt-2 h-12 rounded-md" data-testid="compare-country">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countries.map(c => (
                    <SelectItem key={c.code} value={c.code} data-testid={`compare-country-${c.code}`}>
                      {c.flag} {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {savings > 0 && midRate > 0 && (
              <div className="ra-card p-4 border-[var(--ra-accent)]/30 bg-[var(--ra-accent)]/5 flex items-center gap-3" data-testid="compare-savings-banner">
                <div className="w-10 h-10 rounded-lg bg-[var(--ra-accent)]/15 text-[var(--ra-accent)] flex items-center justify-center">
                  <TrendingDown size={18} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wider text-[var(--ra-muted)] font-semibold">Your family receives</div>
                  <div className="font-heading text-lg font-bold text-[var(--ra-accent)]">
                    +{formatMoney(savings, destCurrency)} more
                  </div>
                  <div className="text-xs text-[var(--ra-muted)]">vs. Western Union on the same ${amount}</div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-8 grid sm:grid-cols-3 gap-4">
          {results.map((r, idx) => (
            <div
              key={r.key}
              className={`ra-card p-5 flex flex-col ${r.primary ? "border-[var(--ra-primary)] bg-[var(--ra-primary)]/[0.03] relative" : ""}`}
              style={{ animationDelay: `${idx * 0.06}s` }}
              data-testid={`compare-card-${r.key}`}
            >
              {r.primary && (
                <div className="absolute -top-2.5 left-5 px-2.5 py-0.5 rounded-full bg-[var(--ra-accent)] text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                  <Sparkles size={10} /> Best value
                </div>
              )}
              <div className="text-xs uppercase tracking-wider text-[var(--ra-muted)] font-semibold mb-1">{r.badge || "Competitor"}</div>
              <h3 className={`font-heading text-lg font-bold ${r.primary ? "text-[var(--ra-primary)]" : ""}`}>{r.name}</h3>

              <div className="mt-5 pb-4 border-b border-[var(--ra-border)]">
                <div className="text-xs uppercase tracking-wider text-[var(--ra-muted)] font-semibold">They receive</div>
                <div className={`font-heading text-2xl font-bold mt-1 ${r.primary ? "text-[var(--ra-primary)]" : ""}`}>
                  {destCurrency && formatMoney(r.received, destCurrency)}
                </div>
              </div>

              <dl className="mt-3 space-y-1.5 text-sm">
                <Row label="Fee">{r.fee === 0 ? "Free" : `${formatMoney(r.fee, "USD")}${r.fee_percent ? ` (${(r.fee_percent * 100).toFixed(0)}%)` : ""}`}</Row>
                <Row label="FX markup">{r.fx_markup ? `${(r.fx_markup * 100).toFixed(1)}%` : "Best rate"}</Row>
                <Row label="Total paid">{formatMoney(r.totalPaid, "USD")}</Row>
                <Row label="Arrives">{r.arrival}</Row>
              </dl>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-[var(--ra-muted)] mt-6 italic">
        * Competitor fees and FX markups based on publicly reported industry averages (US → Africa corridor). Actual costs may vary slightly per transaction.
      </p>
    </section>
  );
};

const Row = ({ label, children }) => (
  <div className="flex justify-between">
    <dt className="text-[var(--ra-muted)]">{label}</dt>
    <dd className="font-medium text-right">{children}</dd>
  </div>
);

export default Compare;
